"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;
var _reactRedux = require("react-redux");
var _setDesiredAttendeeCameraStatus = _interopRequireDefault(require("../attendee/setDesiredAttendeeCameraStatus"));
var _setDesiredAttendeeMuteStatus = _interopRequireDefault(require("../attendee/setDesiredAttendeeMuteStatus"));
var _toggleProgramSource = _interopRequireDefault(require("../program/toggleProgramSource"));
var _setSoloLive = _interopRequireDefault(require("../program/setSoloLive"));
var _checkSourceTypes = require("@studio/sources/checkSourceTypes");
var _getSourcesInProgram = require("@studio/sources/getSourcesInProgram");
var _getSources = require("@studio/sources/getSources");
var _utils = require("../../utils");
var _StudioLogger = require("@studio/logger/StudioLogger");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
var logger = new _StudioLogger.StudioLogger('program-update');

/**
 * Take a presenter live (a presenter is a source, either a cameraPresenter, screenPresenter, audioPresenter source)
 *
 * This action is "at risk" as it is complex and difficult to understand. Consider refactoring.
 *
 * Note that this action dispatches other actions, but does not return a thunk that creates an action
 * Note that this action is named takePresenterLive, but this is a misnomer as the action actually serves to toggle
 *      a camera/audio source live from the program. If the source is live, it is removed, if it is not live, it is added
 *      to the program
 * Note that this action is named takePresenterLive but "presenter" is not in the lexicon of api.v2 (api.v2 knows about sources).
 *      A "presenter" is really a camera source and/or an audio source, which are bound to a chime attendee (as the source value)
 */
function takePresenterLive(source) {
  var sourceActionConfig = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var setProgram = arguments.length > 2 ? arguments[2] : undefined;
  return function (dispatch, getState) {
    var _broadcastSettings$au, _meetingResponse$Meet;
    var _getState = getState(),
      attendeeCameraStatus = _getState.attendeeCameraStatus,
      attendeeMuteStatus = _getState.attendeeMuteStatus,
      _getState$program$lay = _getState.program.layers,
      layers = _getState$program$lay === void 0 ? [] : _getState$program$lay,
      broadcastSettings = _getState.broadcastSettings,
      broadcastSources = _getState.broadcastSources,
      settings = _getState.broadcast.settings,
      _getState$meeting = _getState.meeting,
      meetingResponse = _getState$meeting.meetingResponse,
      attendeeTiles = _getState$meeting.attendeeTiles,
      _getState$isAudioMute = _getState.isAudioMuted,
      isAudioMuted = _getState$isAudioMute === void 0 ? undefined : _getState$isAudioMute;
    var autoMutePresenters = (_broadcastSettings$au = broadcastSettings === null || broadcastSettings === void 0 ? void 0 : broadcastSettings.autoMutePresenters) !== null && _broadcastSettings$au !== void 0 ? _broadcastSettings$au : settings === null || settings === void 0 ? void 0 : settings.autoMutePresenters;
    var isSoloLive = sourceActionConfig.isSoloLive;
    var sourceId = source._id;
    var isLiveSource = (0, _getSourcesInProgram.isSourceInProgram)(layers, sourceId);
    var sourceAction = isSoloLive ? _setSoloLive["default"] : _toggleProgramSource["default"];
    var isScreenShare = (0, _checkSourceTypes.isScreenPresenter)(source);
    var sourceValue = (0, _getSources.getSourceValue)(source);
    var isAttendeeCameraOn = attendeeCameraStatus[sourceValue] ? true : false;
    var isAttendeeMicOn = isAudioMuted !== undefined ? isAudioMuted : Boolean(attendeeMuteStatus === null || attendeeMuteStatus === void 0 ? void 0 : attendeeMuteStatus[sourceValue]);
    var addingSource = !layers.find(function (layer) {
      return layer.source._id === sourceId;
    });
    var sources = (0, _utils.getBroadcastSourcesFromLayers)(layers, broadcastSources === null || broadcastSources === void 0 ? void 0 : broadcastSources.sources);

    // Stage limit (10) - controls mute behavior
    var isStageAtLimit = addingSource ? (0, _utils.willExceedCameraLimit)(sources, source) : false;

    // Camera ON limit (15 Agora / 10 Chime) - controls camera turn-on
    var isCameraOnAtLimit = addingSource ? (0, _utils.hasMeetingExceededCameraLimit)(attendeeTiles, attendeeCameraStatus) : false;
    var meetingId = meetingResponse === null || meetingResponse === void 0 || (_meetingResponse$Meet = meetingResponse.Meeting) === null || _meetingResponse$Meet === void 0 ? void 0 : _meetingResponse$Meet.MeetingId;
    var removedSources = isSoloLive ? sources === null || sources === void 0 ? void 0 : sources.filter(function (src) {
      return src._id !== sourceId;
    }) : !addingSource ? [source] : [];
    removedSources === null || removedSources === void 0 || removedSources.forEach(function (src) {
      if ((0, _checkSourceTypes.isCameraPresenter)(src)) {
        var logPayload = {
          'studio.actionName': isSoloLive ? 'Set Solo Live' : 'Toggle Program Source',
          'studio.attendeeTileStatus': 'OFF-STAGE',
          'studio.tileAttendeeId': src.value,
          title: "ProgramUpdate: Attendee ".concat(src.value, " in meeting ").concat(meetingId, " is removed from the stage")
        };
        logger.info(logPayload.title, logPayload);
      }
    });
    if (!isLiveSource && addingSource && !isStageAtLimit) {
      var logPayload = {
        'studio.actionName': isSoloLive ? 'Set Solo Live' : 'Toggle Program Source',
        'studio.attendeeTileStatus': 'ON-STAGE',
        'studio.tileAttendeeId': source.value,
        title: "ProgramUpdate: Attendee ".concat(source.value, " in meeting ").concat(meetingId, " is added to the stage")
      };
      logger.info(logPayload.title, logPayload);
    }
    var newDesiredMuteStatus;

    /**
     * This action can potentially dispatch many other actions, so we do them in batch for performance reasons (reduce render passes)
     */
    (0, _reactRedux.batch)(function () {
      /**
       * All of this stuff only applies if autoMutePresenters is turned on
       */
      if (autoMutePresenters) {
        /**
         * If the source is _not_ in program, set desired mute status to false (unmuted)
         */
        if (!isLiveSource) {
          newDesiredMuteStatus = false;
        } else if (!isSoloLive) {
          /**
           * If source is in the program, we are taking them out, so set the desired mute status to true (muted)
           */
          newDesiredMuteStatus = true;
        }

        // if we set a camera presenter solo, we want to mute the sources being removed from program.
        if (isSoloLive && (0, _checkSourceTypes.isCameraPresenter)(source)) {
          //why does this only apply to camera_presenter?? why not audio_presenter??
          layers.filter(function (layer) {
            return (0, _checkSourceTypes.isCameraPresenter)(layer.source) && layer.source._id !== source._id;
          }).forEach(function (layer) {
            var source = layer.source;
            var otherSourceValue = (0, _getSources.getSourceValue)(source);
            dispatch((0, _setDesiredAttendeeMuteStatus["default"])(otherSourceValue, true));
          });
        }
      }
      if (isStageAtLimit || !autoMutePresenters) newDesiredMuteStatus = isAttendeeMicOn;

      /**
       * Set the desired attendee camera status to on if it is off.
       *
       *  Do this only if:
       *   - they are _not_ currently in the program
       *   - the source is _not_ a screen share source
       *   - the maximum camera is not reached
       */
      if (!isCameraOnAtLimit && !isAttendeeCameraOn && !isScreenShare && !isLiveSource && !source.cameraUnavailable) {
        dispatch((0, _setDesiredAttendeeCameraStatus["default"])(sourceValue, true));
      }
      dispatch((0, _setDesiredAttendeeMuteStatus["default"])(sourceValue, newDesiredMuteStatus));
      dispatch(sourceAction(source, setProgram));
    });
  };
}
var _default = exports["default"] = takePresenterLive;